import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import CommonSectionTab from "../StatusSubComponents/CommonSectionTab";
import SVGComponent from "../../SVGComponent";
import PrimaryWithIconButton from "../../CommonButtons/PrimaryWithIconButton";

import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";

import "./index.css";
import ICONS from "../../../../assets/Icons";

// Suggestions of props for component while using the components -------------------------------------

/**
 * @typedef {"success" | "error" | "pending" } StatusType
 */

/**
 * @typedef {Object} ButtonConfig
 * @property {string} btnText - Text displayed on the button
 * @property {Function} handleFunction - Function executed on click
 * @property {string} [enabledBgColor]
 * @property {string} [disabledBgColor]
 * @property {string} [enabledTextColor]
 * @property {boolean} [isDisabled]
 * @property {boolean} [isLoading]
 * @property {boolean} [isLoaderNeeded]
 * @property {string} [hoverOption]
 */

/**
 * @param {Object} props
 * @param {StatusType} props.status - Defines UI state (controls icon + background). Example: "success" | "error" | "pending"
 * @param {string} props.title - Main heading text shown in header
 * @param {string} [props.subtitle] - Optional secondary description text
 * @param {React.ReactNode} props.children - Tab content components. Each child should have a `title` prop for tab label
 * @param {boolean} props.isLoading - Controls initial animation visibility & loading state
 * @param {ButtonConfig[][]} props.buttonRows - 2D array of button configs grouped by rows
 * @param {"white" | "black"} [props.hoverOption] - Hover style for tab buttons
 *
 * @returns {JSX.Element}
 */

const StatusContainer = ({
  status,
  title,
  subtitle,
  children,
  isLoading,
  buttonRows,
  hoverOption,
}) => {
  const [show, setShow] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  const prevIndexRef = useRef(0);

  const [animationClass, setAnimationClass] = useState("");

  const scrollPositions = useRef({});
  const scrollRef = useRef(null);

  const bgColor =
    status === "success"
      ? "bg-positive-bg"
      : status === "error"
        ? "bg-negative-bg"
        : status === "pending"
          ? "bg-neutral-bg"
          : "bg-primary-bg";

  const statusConfig = {
    success: {
      iconUrl: ICONS?.SUCCESS_SVG,
    },
    error: {
      iconUrl: ICONS?.FAILED_SVG,
    },
    pending: {
      iconUrl: ICONS?.PENDING_SVG,
    },
  };

  const config = statusConfig[status];

  useEffect(() => {
    let timer;
    if (!isLoading) {
      timer = setTimeout(
        () => setShow(true),
        IntervalConfiguration?.intervalExtraLarge,
      );
    } else {
      setShow(false);
    }
    return () => clearTimeout(timer);
  }, [isLoading]);

  let myChildren;

  if (
    React.Children.toArray(children)[0]?.props?.children?.length >
    GlobalConfiguration?.lengthCheckZero
  ) {
    myChildren = React.Children.toArray(children)
      .map((val) => val?.props?.children)
      .flat();
  } else {
    myChildren = React.Children.toArray(children);
  }

  const mergeButtonRows = (rows) => {
    const updated = rows.map((row) => [...row]);

    for (
      let i = GlobalConfiguration?.lengthCheckZero;
      i < updated.length;
      i++
    ) {
      if (updated[i].length > GlobalConfiguration?.lengthCheckTwo) {
        const extraButtons = updated[i].splice(2);

        if (updated[i + 1]) {
          updated[i + 1] = [...extraButtons, ...updated[i + 1]];
        } else {
          updated[i + 1] = extraButtons;
        }
      }
    }

    if (
      updated[0]?.length === GlobalConfiguration?.lengthCheckOne &&
      updated[1]?.length === GlobalConfiguration?.lengthCheckOne
    ) {
      updated[0] = [...updated[0], ...updated[1]];
      updated.splice(1, 1);
    } else if (
      updated[1]?.length === GlobalConfiguration?.lengthCheckOne &&
      updated.length === GlobalConfiguration?.lengthCheckThree
    ) {
      updated[1] = [...updated[1], ...updated[2]];
      updated.splice(2, 1);
    }

    return updated;
  };

  const updatedRows = mergeButtonRows(buttonRows);
  const rowCount = updatedRows.length;
  const dynamicPadding =
    rowCount === GlobalConfiguration.lengthCheckTwo
      ? GlobalConfiguration.lengthCheckZero
      : GlobalConfiguration.lengthCheckZero;

  // const handleScroll = (e) => {
  //   const el = e.target;
  //   const { scrollTop, scrollHeight, clientHeight } = el;

  //   const isAtTop = scrollTop === GlobalConfiguration.lengthCheckZero;
  //   const isAtBottom =
  //     scrollTop + clientHeight >=
  //     scrollHeight - GlobalConfiguration.lengthCheckOne;

  //   setShowTopShadow(!isAtTop);
  //   setShowBottomShadow(!isAtBottom);
  // };

  const handleScroll = (e) => {
    const el = e.target;

    scrollPositions.current[activeTab] = el.scrollTop;

    const { scrollTop, scrollHeight, clientHeight } = el;

    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    setShowTopShadow(!isAtTop);
    setShowBottomShadow(!isAtBottom);
  };

  useEffect(() => {
    const el = scrollRef.current;

    if (!el) return;

    const savedScroll = scrollPositions.current[activeTab] || 0;

    el.scrollTop = savedScroll;
  }, [activeTab]);

  useLayoutEffect(() => {
    const prevIndex = prevIndexRef.current;

    if (activeTab !== prevIndex) {
      const direction =
        activeTab > prevIndex ? "animate-slide-next" : "animate-slide-prev";

      setAnimationClass("");
      requestAnimationFrame(() => {
        setAnimationClass(direction);
      });

      prevIndexRef.current = activeTab;
    }
  }, [activeTab]);

  return (
    <div
      className={`flex flex-col h-screen overflow-hidden items-center ${bgColor}`}
    >
      <div
        className={`h-navbar-height flex-shrink-0 Qsmd:block hidden ${bgColor}`}
      ></div>

      <div
        className={`flex-1 flex pt-padding-md Qsmd:w-[unset] w-full ${
          show && !isLoading
            ? "statusPageAnimation overflow-y-hidden Qsmd:px-padding-md Qsmd:pt-padding-sm Qsmd:pb-padding-base pt-padding-base"
            : "statusPageAnimationCir"
        }`}
      >
        <div className="flex flex-col gap-gap-sm Qsmd:w-[unset] w-full">
          {/* Header */}
          <div
            className={`flex items-center gap-gap-md ${
              show && !isLoading
                ? "justify-start Qsmd:w-[var(--status-width)] w-full Qsmd:pl-padding-base pl-padding-sm"
                : "justify-center"
            }`}
            style={{ flexDirection: show && !isLoading ? "row" : "column" }}
          >
            <div
              className={`${show && !isLoading && "shrink-animation"} w-graphic-width-md h-graphic-square-height-md flex-shrink-0`}
              style={{ transition: "all 1s linear forwards" }}
            >
              <SVGComponent
                url={config?.iconUrl}
                width={"var(--width-full)"}
                height={"var(--height-full)"}
              />
            </div>

            <div
              className={`flex flex-col gap-gap-xs py-padding-sm ${
                show && !isLoading && "fade-animation"
              } ${show && !isLoading ? " items-start" : "items-center text-center"}`}
            >
              <div className="text-text-primary text-font-md font-semibold">
                {title}
              </div>

              {subtitle && (
                <div className="text-text-secondary text-font-xs font-normal">
                  {subtitle}
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div
            className={`relative flex-1 flex flex-col overflow-hidden rounded-t-border-radius-md ${bgColor} p-padding-sm Qsmd:w-[var(--status-width)] w-full`}
            style={{
              boxShadow: "var(--box-shadow-all)",
              display: show && !isLoading ? "flex" : "none",
            }}
          >
            <div
              className={`${bgColor}`}
              style={{
                boxShadow: showTopShadow ? "0 4px 4px rgba(0,0,0,0.25)" : "",
                overflow: "hidden",
                clipPath: showTopShadow
                  ? "polygon(0.1% 1%, 100% 0, 100% 150%, 0.5px 150%)"
                  : "",
                zIndex: "var(--z-index-header)",
                marginLeft: "-0.5px",
              }}
            >
              <div className="rounded-[var(--border-radius-l)]">
                <CommonSectionTab
                  tabs={myChildren.map((c, i) => ({
                    key: i,
                    label: c?.props?.title || `Tab ${i + 4}`,
                  }))}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  activeBgColor={
                    status === "success"
                      ? "var(--positive-act)"
                      : status === "error"
                        ? "var(--negative-act)"
                        : "var(--neutral-act)"
                  }
                  inactiveBgColor="transparent"
                  hoverOption={hoverOption}
                />
              </div>
            </div>

            <div
              ref={scrollRef}
              className="flex-1 overflow-auto StatusScrollHide"
              onScroll={handleScroll}
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                // paddingBottom: `${dynamicPadding}px`,
                zIndex: "var(--z-index-content)",
              }}
            >
              <div
                key={activeTab}
                className={animationClass}
              >
                {myChildren[activeTab]}
              </div>
            </div>

            {/* Buttons */}
            <div
              className={`${bgColor} `}
              style={{
                boxShadow: showBottomShadow ? "var(--box-shadow-top)" : "",
                clipPath: showBottomShadow
                  ? "polygon(0.1% -25%, 100% -20%, 100% 100%, 0% 100%)"
                  : "",
                zIndex: "var(--z-index-header)",
              }}
            >
              {updatedRows?.length > GlobalConfiguration?.lengthCheckZero && (
                <div
                  className={`pt-padding-sm pb-padding-sm flex flex-col gap-gap-sm`}
                >
                  {updatedRows.map((row, rowIndex) => (
                    <div
                      key={rowIndex}
                      className="flex gap-gap-sm w-full Qsmd:flex-row flex-col"
                    >
                      {row?.map((btn, btnIndex) => (
                        <div
                          key={btnIndex}
                          className="flex-1"
                        >
                          <PrimaryWithIconButton {...btn} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusContainer;
